import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanionCard } from "@/components/CompanionCard";
import { DatePicker } from "@/components/DatePicker";
import { api, getActivities, todayIST } from "@/lib/api";
import { useLang, actLabel } from "@/lib/i18n";

const HOURS = Array.from({ length: 14 }, (_, i) => `${String(i + 9).padStart(2, "0")}:00`);

const GenderTabs = ({ value, onChange }) => {
  const { t } = useLang();
  const opts = [["", t("all")], ["female", t("female")], ["male", t("male")]];
  return (
    <div className="inline-flex rounded-full bg-white border p-1" data-testid="gender-tabs">
      {opts.map(([v, l]) => (
        <button key={v || "all"} data-testid={`gender-tab-${v || "all"}`} onClick={() => onChange(v)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${value === v ? "bg-[#0D5C46] text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
      ))}
    </div>
  );
};

export default function Browse() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [acts, setActs] = useState([]);
  const [items, setItems] = useState(null);
  const get = (k) => params.get(k) || "";
  const set = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    if (k === "date" && !v) p.delete("time");
    setParams(p, { replace: true });
  };

  useEffect(() => {
    getActivities().then(setActs).catch(() => {});
  }, []);
  useEffect(() => {
    setItems(null);
    const q = Object.fromEntries(params.entries());
    api.get("/companions", { params: q }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [params]);

  return (
    <div className="container-x py-10 space-y-8" data-testid="browse-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("filters")}</p>
          <h1 className="text-4xl sm:text-5xl font-bold mt-1">{t("nav_find")}</h1>
        </div>
        <GenderTabs value={get("gender")} onChange={(v) => set("gender", v)} />
      </div>
      <div className="card-surface p-4 grid gap-3 grid-cols-2 lg:grid-cols-6 items-end" data-testid="browse-filters">
        <Input data-testid="filter-city-input" placeholder={t("search_city")} defaultValue={get("city")} onBlur={(e) => set("city", e.target.value.trim())} onKeyDown={(e) => e.key === "Enter" && set("city", e.target.value.trim())} />
        <DatePicker testId="filter-date-input" min={todayIST()} clearable placeholder={t("search_date")} value={get("date")} onChange={(v) => set("date", v)} />
        <Select value={get("time") || "any"} onValueChange={(v) => set("time", v === "any" ? "" : v)} disabled={!get("date")}>
          <SelectTrigger data-testid="filter-time-select"><SelectValue placeholder={t("any_time")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("any_time")}</SelectItem>{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={get("activity") || "any"} onValueChange={(v) => set("activity", v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-activity-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("any_activity")}</SelectItem>{acts.map((a) => <SelectItem key={a.slug} value={a.slug}>{actLabel(a, lang)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={get("max_price") || "any"} onValueChange={(v) => set("max_price", v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-price-select"><SelectValue placeholder={t("max_price")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("max_price")}: {t("all")}</SelectItem>{[500, 600, 700, 1000].map((p) => <SelectItem key={p} value={String(p)}>≤ ₹{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={get("sort") || "rating"} onValueChange={(v) => set("sort", v)}>
          <SelectTrigger data-testid="filter-sort-select"><SlidersHorizontal size={14} /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="rating">Top rated</SelectItem><SelectItem value="price_asc">Price: low → high</SelectItem><SelectItem value="price_desc">Price: high → low</SelectItem></SelectContent>
        </Select>
      </div>
      {items === null ? (
        <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#0D5C46]" /></div>
      ) : items.length === 0 ? (
        <p className="py-20 text-center text-slate-500" data-testid="browse-empty">{t("no_results")}</p>
      ) : (
        <>
          <p className="text-sm text-slate-500" data-testid="browse-count">{items.length} {t("companions_found")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{items.map((c) => <CompanionCard key={c.id} c={c} acts={acts} />)}</div>
        </>
      )}
    </div>
  );
}
